import React, { useEffect, useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { AccountSummary, AssetSummary, IdentitySummary } from "../../../../../database/mediaOps/mediaOpsApi";
import { loadLibraryItems } from "../../../../../database/mediaOps/mediaOpsReads";
import { VideoLibraryItem } from "../../../../../types/Community";
import { useMutation, useOpRead } from "./hooks";
import { assetChip } from "./stateLabels";
import {
  CheckLabel,
  FieldLabel,
  FormGrid,
  Grow,
  Hint,
  ListRow,
  Mono,
  Panel,
  PanelHead,
  ResultNotice,
  Row,
  Select,
  Small,
  TextArea,
  TextInput,
} from "./ui";

// Media assets with provenance. Eligibility is decided by the server for a
// chosen account. The UI only shows the answer and the reasons.

export const AssetsView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const accounts = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const identities = useOpRead(() => mediaOps.listIdentities(communityId), [communityId]);
  const accList: AccountSummary[] = accounts.result?.data?.accounts || [];
  const idList: IdentitySummary[] = identities.result?.data?.identities || [];
  const [identityId, setIdentityId] = useState("");
  const [eligibleFor, setEligibleFor] = useState("");
  const assets = useOpRead(
    () =>
      mediaOps.listAssets(communityId, {
        ...(identityId ? { identityId } : {}),
        ...(eligibleFor ? { eligibleForAccountId: eligibleFor, includeExclusions: true } : {}),
      }),
    [communityId, identityId, eligibleFor]
  );
  const list: AssetSummary[] = assets.result?.data?.assets || [];
  const exclusions = assets.result?.data?.exclusions || [];

  return (
    <>
      <Panel>
        <PanelHead>
          Assets <Grow />
          <Button size="sm" variant="secondary" onClick={assets.reload}>
            Refresh
          </Button>
        </PanelHead>
        <FormGrid>
          <FieldLabel>
            Identity
            <Select value={identityId} onChange={(e) => setIdentityId(e.target.value)}>
              <option value="">All identities</option>
              {idList.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <FieldLabel>
            Eligible for account
            <Select value={eligibleFor} onChange={(e) => setEligibleFor(e.target.value)}>
              <option value="">Any account</option>
              {accList.map((a) => (
                <option key={a.id} value={a.id}>
                  @{a.handle} ({a.platform})
                </option>
              ))}
            </Select>
          </FieldLabel>
        </FormGrid>
        {assets.result && assets.result.status === "blocked" ? <ResultNotice result={assets.result} /> : null}
        {list.length === 0 && assets.result?.status !== "blocked" ? <Small>No assets match.</Small> : null}
        {list.map((a) => (
          <AssetRow key={a.id} communityId={communityId} asset={a} identities={idList} onChanged={assets.reload} />
        ))}
        {exclusions.length > 0 ? (
          <>
            <PanelHead>Not eligible for this account</PanelHead>
            {exclusions.map((x) => (
              <ListRow key={x.assetId}>
                <Mono>{x.assetId}</Mono>
                {x.reasons.map((r, i) => (
                  <Small key={i}>
                    {r.code}: {r.message}
                  </Small>
                ))}
              </ListRow>
            ))}
          </>
        ) : null}
      </Panel>
      <RegisterForm communityId={communityId} identities={idList} onDone={assets.reload} />
      <ImportForm communityId={communityId} identities={idList} onDone={assets.reload} />
    </>
  );
};

const AssetRow: React.FC<{ communityId: string; asset: AssetSummary; identities: IdentitySummary[]; onChanged: () => void }> = ({
  communityId,
  asset: a,
  identities,
  onChanged,
}) => {
  const chip = assetChip(a.status);
  const [attestOpen, setAttestOpen] = useState(false);
  const idName = identities.find((i) => i.id === a.subjectIdentityId)?.name || a.subjectIdentityId;
  const dims = a.width && a.height ? `${a.width}x${a.height}` : "";
  return (
    <ListRow style={{ flexDirection: "column", alignItems: "stretch" }}>
      <Row>
        <Tag size="sm" variant={chip.variant}>
          {chip.label}
        </Tag>
        <Tag size="sm">{a.type}</Tag>
        <Small>Source: {a.sourceKind}</Small>
        <Small>Subject: {idName}</Small>
        {dims ? <Small>{dims}</Small> : null}
        {typeof a.durationSec === "number" ? <Small>{a.durationSec}s</Small> : null}
        <Small>Used {a.reuse?.count ?? 0} times</Small>
        <Grow />
        <Mono>{a.id}</Mono>
      </Row>
      {a.ancestorIds && a.ancestorIds.length > 0 ? <Small>Derived from {a.ancestorIds.join(", ")}</Small> : null}
      {a.reasons && a.reasons.length > 0 ? <Small>Reasons: {a.reasons.join("; ")}</Small> : null}
      {a.status !== "eligible" ? (
        attestOpen ? (
          <AttestForm communityId={communityId} asset={a} identities={identities} onDone={onChanged} />
        ) : (
          <Row>
            <Button size="sm" variant="ghost" onClick={() => setAttestOpen(true)}>
              Attest ownership
            </Button>
          </Row>
        )
      ) : null}
    </ListRow>
  );
};

const AttestForm: React.FC<{ communityId: string; asset: AssetSummary; identities: IdentitySummary[]; onDone: () => void }> = ({
  communityId,
  asset,
  identities,
  onDone,
}) => {
  const [subject, setSubject] = useState(asset.subjectIdentityId || "");
  const [evidence, setEvidence] = useState("");
  const [reason, setReason] = useState("");
  const m = useMutation();
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.attestAsset(k, communityId, {
        assetId: asset.id,
        subjectIdentityId: subject,
        rightsEvidenceRef: evidence.trim(),
        reason: reason.trim(),
        ...(typeof asset.version === "number" ? { expectedVersion: asset.version } : {}),
      })
    );
    if (res && res.status !== "blocked") onDone();
  };
  return (
    <Panel>
      <Hint>Super admin only. Attesting marks this asset as owned media. The reason and evidence are logged.</Hint>
      <FormGrid>
        <FieldLabel>
          Subject identity
          <Select
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              m.edited();
            }}
          >
            {identities.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Rights evidence
          <TextInput
            value={evidence}
            onChange={(e) => {
              setEvidence(e.target.value);
              m.edited();
            }}
          />
        </FieldLabel>
        <FieldLabel>
          Reason
          <TextInput
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              m.edited();
            }}
          />
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" onClick={submit} disabled={!subject || !evidence.trim() || !reason.trim()} isLoading={m.busy}>
          Attest
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Asset attested as owned." />
    </Panel>
  );
};

const RegisterForm: React.FC<{ communityId: string; identities: IdentitySummary[]; onDone: () => void }> = ({
  communityId,
  identities,
  onDone,
}) => {
  const [items, setItems] = useState<VideoLibraryItem[]>([]);
  const [libraryId, setLibraryId] = useState("");
  const [subject, setSubject] = useState("");
  const [evidence, setEvidence] = useState("");
  const m = useMutation<{ assetId?: string; status?: string }>();
  useEffect(() => {
    let alive = true;
    loadLibraryItems(communityId)
      .then((list) => alive && setItems(list))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [communityId]);
  const item = items.find((i) => i.id === libraryId);
  const submit = async () => {
    if (!item) return;
    const res = await m.run((k) =>
      mediaOps.registerOwned(k, communityId, {
        ...(item.mediaAssetId ? { mediaAssetId: item.mediaAssetId } : { libraryAssetId: item.id }),
        subjectIdentityId: subject,
        rightsEvidenceRef: evidence.trim(),
      })
    );
    if (res && res.status !== "blocked") onDone();
  };
  return (
    <Panel>
      <PanelHead>Register owned media</PanelHead>
      <Hint>Pick an upload from the video library. Only a verified upload record can become owned media.</Hint>
      <FormGrid>
        <FieldLabel>
          Library item
          <Select
            value={libraryId}
            onChange={(e) => {
              setLibraryId(e.target.value);
              m.edited();
            }}
          >
            <option value="">Choose an upload</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.type})
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Subject identity
          <Select
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              m.edited();
            }}
          >
            <option value="">Choose an identity</option>
            {identities.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.kind})
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>
      <FieldLabel>
        Rights evidence
        <TextArea
          placeholder="Who recorded it, and when."
          value={evidence}
          onChange={(e) => {
            setEvidence(e.target.value);
            m.edited();
          }}
        />
      </FieldLabel>
      <Row>
        <Button size="sm" onClick={submit} disabled={!item || !subject || !evidence.trim()} isLoading={m.busy}>
          Register
        </Button>
      </Row>
      <ResultNotice
        result={m.result}
        okText={m.result?.data?.assetId ? `Registered as ${m.result.data.assetId} (${m.result.data.status}).` : "Registered."}
      />
    </Panel>
  );
};

const ImportForm: React.FC<{ communityId: string; identities: IdentitySummary[]; onDone: () => void }> = ({
  communityId,
  identities,
  onDone,
}) => {
  const [url, setUrl] = useState("");
  const [type, setType] = useState("image");
  const [batch, setBatch] = useState(() => `manual-${new Date().toISOString().slice(0, 10)}`);
  const [subject, setSubject] = useState("");
  const [ack, setAck] = useState(false);
  const m = useMutation<{ assetId?: string; jobId?: string; status?: string }>();
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    m.edited();
  };
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.importAsset(k, communityId, {
        sourceUrl: url.trim(),
        type,
        importBatchId: batch.trim(),
        ...(subject ? { subjectIdentityId: subject } : {}),
      })
    );
    if (res && res.status !== "blocked") onDone();
  };
  return (
    <Panel>
      <PanelHead>Import by URL</PanelHead>
      <Hint>An imported file starts quarantined. A URL never proves ownership.</Hint>
      <FormGrid>
        <FieldLabel>
          Source URL
          <TextInput value={url} onChange={(e) => edit(setUrl)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Type
          <Select value={type} onChange={(e) => edit(setType)(e.target.value)}>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
          </Select>
        </FieldLabel>
        <FieldLabel>
          Import batch
          <TextInput value={batch} onChange={(e) => edit(setBatch)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Subject identity (optional)
          <Select value={subject} onChange={(e) => edit(setSubject)(e.target.value)}>
            <option value="">Unknown</option>
            {identities.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>
      <CheckLabel>
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />I understand this asset stays quarantined until it is attested.
      </CheckLabel>
      <Row>
        <Button size="sm" onClick={submit} disabled={!url.trim() || !batch.trim() || !ack} isLoading={m.busy}>
          Import
        </Button>
      </Row>
      <ResultNotice
        result={m.result}
        okText={m.result?.data?.assetId ? `Imported as ${m.result.data.assetId}. Status ${m.result.data.status}.` : "Import queued."}
      />
      {m.result?.data?.jobId ? (
        <Small>
          Import job <Mono>{m.result.data.jobId}</Mono>. Status {m.result.data.status || "quarantined"}.
        </Small>
      ) : null}
    </Panel>
  );
};

export default AssetsView;
